import { apiClient } from '@/hooks/useApi';

// In-app notification (for NotificationList / NotificationItem pages)
export interface Notification {
  notificationId: string;
  type: 'Info' | 'Success' | 'Warning' | 'Error';
  title: string;
  body: string;
  isRead: boolean;
  createdAt: string;
}

// SMS queue notification (for NotificationCenter bell icon)
export interface PendingNotification {
  notificationId: string;
  customerName: string;
  workOrderNumber: string;
  message: string;
  phoneNumber: string;
  createdAt: string;
  status: 'Pending' | 'Sent' | 'Failed' | 'Cancelled';
  attemptCount: number;
  lastError?: string;
  sentAt?: string;
}

export const notificationsApi = {
  // ── In-app notifications (NotificationList page) ──────────────────────────
  async list(unreadOnly?: boolean): Promise<Notification[]> {
    const { data } = await apiClient.get<Notification[]>('/api/notifications', {
      params: unreadOnly ? { unreadOnly: true } : undefined,
    });
    return data;
  },

  async markRead(id: string): Promise<void> {
    await apiClient.patch(`/api/notifications/${id}/read`);
  },

  async markAllRead(): Promise<void> {
    await apiClient.post('/api/notifications/mark-all-read');
  },

  // ── SMS queue (NotificationCenter bell icon) ──────────────────────────────
  async getPending(): Promise<PendingNotification[]> {
    const { data } = await apiClient.get<PendingNotification[]>('/api/notifications/pending');
    return data;
  },

  async getHistory(): Promise<PendingNotification[]> {
    const { data } = await apiClient.get<PendingNotification[]>('/api/notifications/history');
    return data;
  },

  async send(id: string): Promise<void> {
    await apiClient.post(`/api/notifications/${id}/send`);
  },

  async cancel(id: string): Promise<void> {
    await apiClient.delete(`/api/notifications/${id}`);
  },

  async sendBatch(): Promise<{ sent: number; failed: number; remaining: number }> {
    const { data } = await apiClient.post<{ sent: number; failed: number; remaining: number }>('/api/notifications/send-batch');
    return data;
  },
};
